import { createMemo } from 'solid-js'
import { authStore } from '~/stores/auth'
import type { PermissionName } from '~/utils/permissions'

/**
 * Hook de permisos. Lee el usuario del store de autenticación y expone:
 * - `isRoot()`: si el usuario es root (acceso total).
 * - `isOwner()`: si es propietario de la empresa activa.
 * - `can(name)`: si el usuario tiene el permiso, con soporte de wildcards (`*`, `builder.*`).
 *
 * El backend ya aplica la regla "eliminar requiere actualizar" al calcular los permisos,
 * así que aquí solo verificamos membresía en el set.
 */
export function usePermissions() {
  const user = () => authStore.user
  const permissions = createMemo(() => {
    const perms = user()?.permissions
    return Array.isArray(perms) ? new Set(perms as string[]) : new Set<string>()
  })
  // Root es root solo por bandera del usuario. El backend trata el wildcard
  // total `*` como acceso amplio pero NO como root: las secciones ROOT_ONLY
  // (Configuración IA, correo, empresas) solo pasan con is_root real, y el
  // frontend debe coincidir o el usuario entra a una sección que la API le
  // responde con 401.
  const isRoot = () => user()?.isRoot === true

  /**
   * Propietario de la **empresa activa**. Ser owner en otra empresa no cuenta:
   * es la misma regla que aplica `isCompanyOwner` en el backend.
   */
  const isOwner = createMemo(() => {
    const activeId = authStore.companyId
    if (!activeId) return false
    return (authStore.companies ?? []).some(c => {
      const id = (c as { companyId?: string }).companyId ?? c.company?.id
      return id === activeId && c.role === 'owner'
    })
  })

  /**
   * Verifica si el usuario tiene el permiso. Soporta wildcards:
   * - `*` → cualquier permiso (equivalente a isRoot en la práctica).
   * - `module.*` → cualquier permiso del módulo (ej: `builder.*`).
   * - `module.action` → permiso exacto.
   *
   * El propietario de la empresa activa pasa siempre: es lo que ya hacía el
   * macro `requirePermission` del backend, que lo deja entrar aunque no tenga
   * ningún rol asignado (el caso de quien se registra y estrena su empresa).
   * Mientras el frontend no lo contemplaba, ese owner veía "No tienes permiso
   * para ver esta sección" en pantallas que la API sí le respondía, y el menú
   * lateral se quedaba en Dashboard y Parámetros.
   *
   * Las secciones sólo-root no pasan por aquí: el layout las comprueba con
   * `isRoot()`, así que el owner sigue sin entrar en ellas.
   */
  const can = (name: PermissionName): boolean => {
    if (isRoot() || isOwner()) return true
    const perms = permissions()
    if (perms.size === 0) return false

    // Coincidencia exacta
    if (perms.has(name)) return true

    // Wildcards: verificar si algún permiso en el set cubre el nombre pedido
    for (const p of perms) {
      if (p === '*') return true
      if (p.endsWith('.*')) {
        const prefix = p.slice(0, -1) // "builder.*" → "builder."
        if (name.startsWith(prefix)) return true
      }
    }
    return false
  }

  return { isRoot, isOwner, can, permissions }
}
